import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import Iframe from 'react-iframe';
import './PdfViewer.css';
import ImageButton from 'components/shared/button/ImageButton';
import closeSvg from 'images/close.svg';
import downloadSvg from 'images/download.svg';
import pdfSvg from 'images/pdf.svg';
import xlsSvg from 'images/xls.svg';

/**
 * Modal PDF viewer with an embedded download-options icon.
 *
 * The download icon opens a small dropdown with two options:
 *   - "Download as PDF" — re-triggers the parent's onDownload('pdf') handler
 *   - "Download as XLSX" — re-triggers the parent's onDownload('xlsx') handler
 *
 * The parent (e.g. ModalRfaLogPdf) is responsible for actually fetching
 * the file with the right filters and triggering the browser download.
 * The viewer just exposes the UI for choosing the format.
 */
export default function PdfViewer(props) {
  const handleClose = () => {
    if (props.onClose) {
      props.onClose();
    }
    setShowDownloadMenu(false);
  };

  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadRef = useRef(null);

  // Close the download menu on outside click
  useEffect(() => {
    if (!showDownloadMenu) return undefined;
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

  const handleDownloadClick = () => {
    if (!props.onDownload) return;
    setShowDownloadMenu((prev) => !prev);
  };

  const handleDownloadFormat = (format) => {
    setShowDownloadMenu(false);
    if (props.onDownload) {
      props.onDownload(format);
    }
  };

  let url = null;
  if (props.content !== null) {
    if (props.content.type === 'url') {
      url = props.content.source;
    } else if (props.content.type === 'blob') {
      if (navigator.appVersion.toString().indexOf('.NET') > 0) {
        window.navigator.msSaveBlob(props.content.source);
        return null;
      } else {
        //Create a Blob from the PDF Stream
        const file = new Blob([props.content.source], {
          type: 'application/pdf',
        });
        //Build a URL from the file
        url = URL.createObjectURL(file);
      }
    }
  }

  // Tidy up Chrome's built-in PDF viewer chrome while keeping the toolbar
  // (download, print, save-to-drive, zoom) intact.
  //
  // - #navpanes=0   hides the side panel (bookmarks/thumbnails)
  // - #statusbar=0  hides the bottom status bar (page count)
  //
  // We deliberately do NOT pass #toolbar=0 — the user wants Chrome's
  // built-in viewer features (notably the Save to Google Drive button)
  // to remain available. Our custom Download dropdown in the modal
  // header is the primary download surface, but Chrome's toolbar
  // stays as a secondary option.
  //
  // Both fragments are honored by Chrome / Edge PDF viewer and are a
  // no-op on Firefox / Safari (which don't render PDFs inline anyway).
  // https://chromium.googlesource.com/chromium/src/+/main/chrome/browser/resources/pdf/open_pdf_params.md
  if (url) {
    const params = 'navpanes=0&statusbar=0';
    if (url.includes('#')) {
      const [base, frag] = url.split('#', 2);
      const existing = new URLSearchParams(frag);
      const merged = new URLSearchParams(params);
      existing.forEach((v, k) => merged.set(k, v));
      url = `${base}#${merged.toString()}`;
    } else {
      url = `${url}#${params}`;
    }
  }

  return (
    <Modal className="pdf-viewer-modal" show={props.show} onHide={handleClose}>
      <Modal.Header>
        <div className="body-bold">{props.title || 'Pdf Viewer'}</div>
        <div className="pdf-viewer-header-actions">
          {props.onDownload && (
            <div className="pdf-viewer-download-wrapper" ref={downloadRef}>
              <ImageButton
                alt="download"
                src={downloadSvg}
                onClick={handleDownloadClick}
                title="Download as…"
              />
              {showDownloadMenu && (
                <div className="pdf-viewer-download-menu">
                  <button
                    type="button"
                    className="pdf-viewer-download-option"
                    onClick={() => handleDownloadFormat('pdf')}
                  >
                    <img
                      src={pdfSvg}
                      alt="PDF"
                      className="pdf-viewer-download-icon"
                    />
                    <div className="pdf-viewer-download-text">
                      <span className="pdf-viewer-download-label">
                        Download as PDF
                      </span>
                      <span className="pdf-viewer-download-hint">
                        Save the current view
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="pdf-viewer-download-option"
                    onClick={() => handleDownloadFormat('xlsx')}
                  >
                    <img
                      src={xlsSvg}
                      alt="XLSX"
                      className="pdf-viewer-download-icon"
                    />
                    <div className="pdf-viewer-download-text">
                      <span className="pdf-viewer-download-label">
                        Download as XLSX
                      </span>
                      <span className="pdf-viewer-download-hint">
                        Excel spreadsheet
                      </span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
          <ImageButton alt="close" src={closeSvg} onClick={handleClose} />
        </div>
      </Modal.Header>
      <Modal.Body>
        {url !== null && (
          <Iframe
            url={url}
            className="pdf-viewer-iframe"
            width="100%"
            height="100%"
            display="initial"
            position="relative"
            allowFullScreen
          />
        )}
      </Modal.Body>
    </Modal>
  );
}

PdfViewer.propTypes = {
  show: PropTypes.bool,
  title: PropTypes.string,
  content: PropTypes.shape({
    source: PropTypes.any,
    type: PropTypes.oneOf(['url', 'blob']),
  }),
  onClose: PropTypes.func,
  // Optional: when provided, shows the download icon. Callback receives
  // 'pdf' or 'xlsx' as the format the user picked.
  onDownload: PropTypes.func,
};

PdfViewer.defaultProps = {
  show: false,
  title: 'Pdf Viewer',
  content: null,
};
